/**
 * Sync log history fixtures for Settings › Collectors sync-log section.
 * Keyed by collectorId so the hook can look up per-collector history.
 * All timestamps derive from FROZEN_NOW.
 */

import { FROZEN_NOW_MS } from '@/lib/frozenTime';

export type SyncLogStatus = 'success' | 'partial' | 'failed';

export interface SyncLogRow {
  id: string;
  status: SyncLogStatus;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  recordsFetched: number;
  recordsUpserted: number;
  errorReason: string | null;
}

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

function makeRow(
  id: string,
  offsetMs: number,
  durationMs: number,
  status: SyncLogStatus,
  fetched: number,
  upserted: number,
  errorReason: string | null,
): SyncLogRow {
  const startedAt = new Date(FROZEN_NOW_MS - offsetMs);
  const finishedAt = new Date(startedAt.getTime() + durationMs);
  return {
    id,
    status,
    startedAt: startedAt.toISOString(),
    finishedAt: finishedAt.toISOString(),
    durationMs,
    recordsFetched: fetched,
    recordsUpserted: upserted,
    errorReason,
  };
}

/**
 * Two collectors seeded so the mockup-mode pagination UI exercises a
 * "Load more" click: `col-insightful-prod` has 15 rows (two pages at
 * limit=10), `col-insightful-archive` has 8 rows (single page). The
 * failed rows carry realistic but clearly-fictional error reasons —
 * invented data only per web/frontend/CLAUDE.md §Invented data only.
 */
export const SYNC_LOGS: Record<string, SyncLogRow[]> = {
  'col-insightful-prod': [
    makeRow('sl-prod-00', 45 * 1000, 28_500, 'success', 245, 238, null),
    makeRow('sl-prod-01', 1 * HOUR_MS, 31_200, 'success', 220, 218, null),
    makeRow('sl-prod-02', 2 * HOUR_MS, 29_800, 'success', 251, 244, null),
    makeRow(
      'sl-prod-03',
      3 * HOUR_MS,
      32_400,
      'partial',
      180,
      160,
      '20 events skipped: schema mismatch',
    ),
    makeRow('sl-prod-04', 4 * HOUR_MS, 30_100, 'success', 201, 195, null),
    makeRow('sl-prod-05', 5 * HOUR_MS, 28_900, 'success', 186, 184, null),
    makeRow('sl-prod-06', 6 * HOUR_MS, 33_600, 'failed', 0, 0, 'Authentication failed'),
    makeRow('sl-prod-07', 7 * HOUR_MS, 30_800, 'success', 215, 210, null),
    makeRow('sl-prod-08', 8 * HOUR_MS, 29_500, 'success', 198, 192, null),
    makeRow('sl-prod-09', 9 * HOUR_MS, 45_000, 'failed', 12, 0, 'Network error'),
    makeRow('sl-prod-10', 10 * HOUR_MS, 30_200, 'success', 230, 224, null),
    makeRow('sl-prod-11', 11 * HOUR_MS, 29_700, 'success', 205, 198, null),
    makeRow('sl-prod-12', 12 * HOUR_MS, 31_400, 'success', 225, 219, null),
    makeRow(
      'sl-prod-13',
      13 * HOUR_MS,
      28_800,
      'partial',
      160,
      150,
      '10 events skipped: duplicate fingerprint',
    ),
    makeRow('sl-prod-14', 14 * HOUR_MS, 30_600, 'success', 240, 232, null),
  ],
  'col-insightful-archive': [
    makeRow('sl-arc-00', 3 * MINUTE_MS, 19_400, 'success', 82, 79, null),
    makeRow('sl-arc-01', 2 * HOUR_MS, 18_900, 'success', 76, 73, null),
    makeRow('sl-arc-02', 4 * HOUR_MS, 20_100, 'success', 88, 85, null),
    makeRow('sl-arc-03', 6 * HOUR_MS, 19_700, 'success', 79, 76, null),
    makeRow('sl-arc-04', 8 * HOUR_MS, 18_500, 'success', 80, 77, null),
    makeRow('sl-arc-05', 10 * HOUR_MS, 21_000, 'failed', 0, 0, 'Rate limited'),
    makeRow('sl-arc-06', 12 * HOUR_MS, 19_200, 'success', 85, 82, null),
    makeRow('sl-arc-07', 14 * HOUR_MS, 18_800, 'success', 77, 74, null),
  ],
  'col-insightful-legacy': [
    makeRow('sl-leg-00', 6 * HOUR_MS, 15_200, 'failed', 0, 0, 'Authentication failed'),
    makeRow('sl-leg-01', 24 * HOUR_MS, 18_400, 'success', 120, 118, null),
    makeRow('sl-leg-02', 48 * HOUR_MS, 17_900, 'success', 115, 112, null),
  ],
};
